import {
	RiArrowDownSLine,
	RiArrowUpSLine,
	RiExpandUpDownLine,
} from '@remixicon/react'
import { type Column } from '@tanstack/react-table'
import {
	createContext,
	type HTMLAttributes,
	type PropsWithChildren,
	useContext,
} from 'react'

import { cn } from '#app/utils/misc.tsx'

interface Props<TData, TValue> extends HTMLAttributes<HTMLDivElement> {
	column: Column<TData, TValue>
	title: string
}

type RequestSortHandler = (columnId: string) => void

const DataTableSortContext = createContext<RequestSortHandler | null>(null)

export function DataTableSortProvider({
	value,
	children,
}: PropsWithChildren<{ value: RequestSortHandler }>) {
	return (
		<DataTableSortContext.Provider value={value}>
			{children}
		</DataTableSortContext.Provider>
	)
}

export function DataTableColumnHeader<TData, TValue>({
	column,
	title,
	className,
}: Props<TData, TValue>) {
	const columnMeta = column.columnDef.meta as { sortable?: boolean } | undefined
	const requestSort = useContext(DataTableSortContext)
	const canSort =
		column.getCanSort() ||
		column.columnDef.enableSorting === true ||
		columnMeta?.sortable
	if (!canSort) {
		return <div className={cn(className)}>{title}</div>
	}

	const sortDirection = column.getIsSorted()
	const handleToggleSorting = () => {
		if (requestSort && typeof column.id === 'string') {
			requestSort(column.id)
			return
		}
		column.toggleSorting(undefined, false)
	}

	return (
		<div
			onClick={handleToggleSorting}
			className={cn(
				'-mx-2 inline-flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 select-none hover:bg-gray-50 hover:dark:bg-gray-900',
				className,
			)}
			role="button"
			tabIndex={0}
			onKeyDown={(event) => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault()
					handleToggleSorting()
				}
			}}
		>
			<span>{title}</span>
			{canSort ? (
				<div className="ml-1">
					{sortDirection === 'asc' ? (
						<RiArrowUpSLine
							className="size-3.5 text-gray-900 dark:text-gray-50"
							aria-hidden="true"
						/>
					) : sortDirection === 'desc' ? (
						<RiArrowDownSLine
							className="size-3.5 text-gray-900 dark:text-gray-50"
							aria-hidden="true"
						/>
					) : (
						<RiExpandUpDownLine
							className="size-3.5 text-gray-400 dark:text-gray-500"
							aria-hidden="true"
						/>
					)}
				</div>
			) : null}
		</div>
	)
}
